// Advertising consent: region detection, visitor choice storage, and gating
// of the Meta pixel. The banner is shown only in regions that require
// consent; everywhere else measurement runs without a banner.
import { grantMetaConsent, loadMetaPixel, revokeMetaConsent } from "./meta-pixel";

export type ConsentChoice = "granted" | "denied";

export interface ConsentState {
  requiresConsent: boolean;
  choice: ConsentChoice | null;
}

const STORAGE_KEY = "fo-ad-consent";

// EEA + UK + Switzerland.
const CONSENT_REGIONS = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR",
  "HU", "IS", "IE", "IT", "LV", "LI", "LT", "LU", "MT", "NL", "NO", "PL",
  "PT", "RO", "SK", "SI", "ES", "SE", "GB", "UK", "CH",
]);

let regionPromise: Promise<string> | null = null;
const listeners = new Set<() => void>();

// Reads the visitor country from Cloudflare's same-origin trace endpoint.
// On failure, non-OK, unknown (XX) or Tor (T1) we treat the visitor as being
// in a consent region and show the banner.
function detectRegion(): Promise<string> {
  if (regionPromise) return regionPromise;
  regionPromise = (async () => {
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 2000);
      const res = await fetch("/cdn-cgi/trace", { signal: ctrl.signal });
      clearTimeout(timer);
      if (!res.ok) return "XX";
      const match = (await res.text()).match(/^loc=(.+)$/m);
      return (match?.[1] ?? "XX").trim().toUpperCase();
    } catch {
      return "XX";
    }
  })();
  return regionPromise;
}

export function getStoredConsent(): ConsentChoice | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "granted" || value === "denied" ? value : null;
  } catch {
    return null;
  }
}

export function onConsentChange(callback: () => void): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function applyConsent(choice: ConsentChoice) {
  if (choice === "granted") {
    grantMetaConsent();
    loadMetaPixel();
  } else {
    revokeMetaConsent();
  }
}

// Detects the region once per page visit, restores any stored choice and
// applies it to the pixel. Outside consent regions measurement is enabled
// without a banner.
export async function initConsent(): Promise<ConsentState> {
  const region = await detectRegion();
  const requiresConsent = region === "XX" || region === "T1" || CONSENT_REGIONS.has(region);
  const choice = getStoredConsent();
  applyConsent(requiresConsent ? (choice ?? "denied") : "granted");
  return { requiresConsent, choice };
}

// Persists the visitor's choice and applies it immediately.
export function setConsent(choice: ConsentChoice) {
  try {
    localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    // Storage unavailable — the choice still applies for this page view.
  }
  applyConsent(choice);
  listeners.forEach((cb) => cb());
}
